// @react-compiler enable
import {testIdProps} from '@github-ui/test-id-props'
import {memo} from 'react'

import type {TitleValueWithContentType} from '../../../api/columns/contracts/title'
import {useHorizontalGroupedBy} from '../../../features/grouping/hooks/use-horizontal-grouped-by'
import {useTableHierarchy} from '../../../features/sub-issues/use-table-hierarchy'
import {isNumber, parseTitleNumber} from '../../../helpers/parsing'
import {useTableSidePanel} from '../../../hooks/use-side-panel'
import {type ColumnValue, hasValue} from '../../../models/column-value'
import type {MemexItemModel} from '../../../models/memex-item-model'
import {MemexItemIcon} from '../../common/memex-item-icon'
import {TextPlaceholder} from '../../common/placeholders'
import {InteractiveItemTitle} from '../../interactive-item-title'
import {BaseCell} from '../cells/base-cell'
import {ExpandButton, type ExpandButtonProps} from '../expand-button'
import {useRecordCellRenderer} from '../performance-measurements'
import styles from './title-renderer.module.css'

type Props = Readonly<
  {
    currentValue: ColumnValue<TitleValueWithContentType>
    model: MemexItemModel
    isDisabled?: boolean
    rowId: string
    /** If defined, represents the number of levels deep this item is in a sub-issues hierarchy. */
    depth?: number
  } & Omit<ExpandButtonProps, 'issueId'>
>

export const LoadingTitleCell = () => (
  <BaseCell>
    <TextPlaceholder minWidth={80} maxWidth={200} {...testIdProps('placeholder')} />
  </BaseCell>
)

export const TitleRenderer = memo(function TitleRenderer({
  currentValue,
  model,
  isDisabled,
  depth = 0,
  expandable,
  expanded,
  rowId,
  expandRef,
}: Props) {
  useRecordCellRenderer('TitleRenderer', model.id)
  const titleValue = hasValue(currentValue) ? currentValue.value : undefined
  const number = parseTitleNumber(titleValue)
  const {openPane} = useTableSidePanel()
  const {hierarchyEnabled} = useTableHierarchy()
  const issueId = model.contentType === 'Issue' ? model.content.id.toString() : undefined
  const {groupedByColumnId} = useHorizontalGroupedBy()
  const isTableGrouped = groupedByColumnId !== undefined
  // If the table is grouped, subtract 1 from the depth to account for the top-level groups
  const renderedDepth = Math.max(0, isTableGrouped ? depth - 1 : depth)

  return (
    <BaseCell>
      {hierarchyEnabled && (
        <div
          style={{'--hierarchy-depth': renderedDepth, '--expandable': expandable ? 1 : 0}}
          className={styles.expandButtonContainer}
        >
          <ExpandButton
            expandRef={expandRef}
            expanded={expanded}
            expandable={expandable}
            issueId={issueId}
            nwoReference={model.getNameWithOwnerReference()}
            rowId={rowId}
          />
        </div>
      )}
      <div className={styles.Box}>
        <MemexItemIcon titleColumn={currentValue} isBlocked={model.isBlocked()} />
      </div>
      <InteractiveItemTitle model={model} isDisabled={isDisabled} currentValue={currentValue} openPane={openPane} />
      {isNumber(number) && (
        <div className={styles.Box_1}>
          <span className={styles.Text}> #{number}</span>
        </div>
      )}
    </BaseCell>
  )
})

try{ LoadingTitleCell.displayName ||= 'LoadingTitleCell' } catch {}
try{ TitleRenderer.displayName ||= 'TitleRenderer' } catch {}